import React, { useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Breadcrumb } from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import StatisticsCard from "../components/statistics-card";
import { ChevronDown, ChevronUp, Wallet, Wrench } from "lucide-react";
import DataTable, { TableRowData } from "../components/data-table";
import {
  activitiesTableColumns,
  invoicesTableColumns,
  quotesTableColumns,
  siteInventoryTableColumns,
} from "../lib/table-columns";
import {
  getSite,
  getSiteInventoryItems,
  getSiteDetailsStatistics,
  getSiteMovements,
  getMovementActivity,
  Site,
  SiteInventoryItem,
  CustomerQuotation,
  CustomerInvoice,
  SiteDetailsStatistics,
  MovementActivity,
  extendMovementDaysHired,
} from "../lib/api";
import { type Inventory } from "../lib/api";
import {
  cn,
  getBackendErrorMessage,
  getMovementDestinationDisplayName,
  getMovementSourceDisplayName,
  getQuantityColumnName,
  movementExcelFromLabel,
  movementQuantitySignForSite,
} from "../lib/utils";
import ActivityDetailsDrawer from "../components/activity-details-drawer";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import BarGraph from "../components/bar-graph";
import ItemDetailsDrawer from "../components/item-details-drawer";
import SelectInput from "../components/select-input";
import InvoiceDrawer from "../components/invoice-drawer";
import QuoteDrawer from "../components/quote-drawer";
import { format } from "date-fns";
import PageLoadingState from "../components/page-loading-state";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import { Modal } from "../components/ui/modal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { updateQuotationStatus, updateInvoice } from "../lib/api";
import { Edit2, Download } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import type { SitesFiltersState } from "./Sites";
import { useIsMobile } from "../hooks/use-is-mobile";
import type { StatItem } from "../components/mobile/types";
import SiteDetailsMobile from "./site-details/SiteDetailsMobile";
import {
  canExtendMovementDaysHired,
  formatExtendableMovementLabel,
  getDefaultExtensionDateFrom,
  getExtensionMinDate,
  inclusiveDaysBetween,
  isExtensionPeriodInvalid,
} from "./site-details/extend-days";

/** Total columns: NO + DESC + SIZE + (one QUANTITY per movement) + TOTAL + UNIT WT + TOTAL WT = 6 + M */
function movementExcelWidth(movementCount: number): number {
  return 6 + Math.max(0, movementCount);
}

function padMovementExcelRow(
  cells: (string | number)[],
  len: number,
): (string | number)[] {
  const row = [...cells];
  while (row.length < len) row.push("");
  return row.slice(0, len);
}

function formatMovementItemSize(length: unknown): string {
  if (
    length == null ||
    length === "" ||
    String(length).toLowerCase() === "undefined"
  ) {
    return "";
  }
  return String(length).trim();
}

type AggregatedMovementMaterial = {
  description: string;
  size: string;
  /** quantity[index] = total qty for this material on sortedMovements[index] */
  quantityByMovement: number[];
  unitWeight: number | null;
};

function sortMovementsForExcel(
  movements: MovementActivity[],
): MovementActivity[] {
  return [...movements].sort((a, b) => {
    const ta = new Date(a.plannedDate || a.createdAt).getTime();
    const tb = new Date(b.plannedDate || b.createdAt).getTime();
    return (
      ta - tb ||
      String(a.reference ?? "").localeCompare(String(b.reference ?? ""))
    );
  });
}

/**
 * One row per material (description + size), with one quantity column per movement
 * in the same order as `sortedMovements`. Quantities are signed for net-on-site (returns etc. subtract).
 */
function aggregateMovementItemsByMovements(
  sortedMovements: MovementActivity[],
  siteId: string,
): AggregatedMovementMaterial[] {
  const M = sortedMovements.length;
  const map = new Map<string, AggregatedMovementMaterial>();
  const signs = sortedMovements.map((m) =>
    movementQuantitySignForSite(m, siteId),
  );

  sortedMovements.forEach((movement, mi) => {
    const sign = signs[mi] ?? 0;
    for (const item of movement.items ?? []) {
      const inv = item.inventory;
      const rawDesc = (item.particulars ?? inv?.description ?? "").trim();
      if (!rawDesc) continue;
      const size = formatMovementItemSize(inv?.length);
      const mergeKey = `${rawDesc.toLowerCase()}|${size.toLowerCase()}`;
      const rawQty =
        Number(item.totalQuantity) || Number(item.goodQuantity) || 0;
      const lineQty = rawQty * sign;
      const uwRaw = inv?.unitWeight;
      const uwNum =
        uwRaw != null && uwRaw !== "" && String(uwRaw) !== "undefined"
          ? Number(uwRaw)
          : NaN;
      const uw = Number.isFinite(uwNum) ? uwNum : null;

      const existing = map.get(mergeKey);
      if (!existing) {
        const quantityByMovement = Array(M).fill(0);
        quantityByMovement[mi] = lineQty;
        map.set(mergeKey, {
          description: rawDesc.toUpperCase(),
          size,
          quantityByMovement,
          unitWeight: uw,
        });
      } else {
        existing.quantityByMovement[mi] += lineQty;
        if (existing.unitWeight == null && uw != null) existing.unitWeight = uw;
      }
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    const d = a.description.localeCompare(b.description);
    return d !== 0 ? d : a.size.localeCompare(b.size);
  });
}

const SiteDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const siteId = location.state?.id;
  const [selectedItemDetails, setSelectedItemDetails] =
    useState<Inventory | null>(null);
  const [selectedActivityDetails, setSelectedActivityDetails] =
    useState<TableRowData | null>(null);
  const [selectedQuoteDetails, setSelectedQuoteDetails] =
    useState<TableRowData | null>(null);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] =
    useState<TableRowData | null>(null);
  const [showEquipments, setShowEquipments] = useState(false);
  const [showMovements, setShowMovements] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [selectedSite, setSelectedSite] = useState<string>(
    location.state?.site_name || "Select Site",
  );
  const queryClient = useQueryClient();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingQuote, setRejectingQuote] = useState<TableRowData | null>(
    null,
  );
  const [isRejecting, setIsRejecting] = useState(false);

  // Approve modal state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvingQuote, setApprovingQuote] = useState<TableRowData | null>(
    null,
  );
  const [isApproving, setIsApproving] = useState(false);

  // Mark as paid modal state
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markingInvoice, setMarkingInvoice] = useState<TableRowData | null>(
    null,
  );
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const [movementsPage, setMovementsPage] = useState(1);
  const [movementsPageSize, setMovementsPageSize] = useState(10);
  const [movementSubTypeFilter, setMovementSubTypeFilter] = useState("all");
  const [movementStatusFilter, setMovementStatusFilter] = useState("all");

  const [showExtendDaysModal, setShowExtendDaysModal] = useState(false);
  const [extendableMovements, setExtendableMovements] = useState<
    MovementActivity[]
  >([]);
  const [loadingExtendableMovements, setLoadingExtendableMovements] =
    useState(false);
  const [selectedExtendMovementIds, setSelectedExtendMovementIds] = useState<
    string[]
  >([]);
  const [bulkExtendDateTo, setBulkExtendDateTo] = useState("");
  const [bulkExtendNotes, setBulkExtendNotes] = useState("");
  const [isExtendingDaysHired, setIsExtendingDaysHired] = useState(false);

  const siteQuery = useQuery<Site>({
    queryKey: ["site", siteId],
    queryFn: async () => getSite(siteId as string),
    enabled: Boolean(siteId),
  });

  const inventoryItemsQuery = useQuery<SiteInventoryItem[]>({
    queryKey: ["site", siteId, "inventory-items"],
    queryFn: async () => getSiteInventoryItems(siteId as string),
    enabled: Boolean(siteId),
  });

  const statisticsQuery = useQuery<SiteDetailsStatistics>({
    queryKey: ["site", siteId, "statistics"],
    queryFn: async () => getSiteDetailsStatistics(siteId as string),
    enabled: Boolean(siteId),
  });

  const movementSubTypeParam =
    movementSubTypeFilter !== "all" ? movementSubTypeFilter : undefined;
  const movementStatusParam =
    movementStatusFilter !== "all" ? movementStatusFilter : undefined;

  const movementsQuery = useQuery({
    queryKey: [
      "site",
      siteId,
      "movements",
      {
        page: movementsPage,
        limit: movementsPageSize,
        activitySubType: movementSubTypeParam,
        status: movementStatusParam,
      },
    ],
    queryFn: async () =>
      getSiteMovements(
        siteId as string,
        movementsPage,
        movementsPageSize,
        movementSubTypeParam,
        movementStatusParam,
      ),
    enabled: Boolean(siteId),
    placeholderData: keepPreviousData,
  });

  const site = siteQuery.data ?? null;
  const inventoryItems = inventoryItemsQuery.data ?? [];
  const statistics = statisticsQuery.data ?? null;

  // Keep selected site name in sync when query resolves.
  useEffect(() => {
    if (site?.name) setSelectedSite(site.name);
  }, [site?.name]);

  // Reset movements pagination when filters change.
  useEffect(() => {
    setMovementsPage(1);
  }, [movementSubTypeFilter, movementStatusFilter]);

  const quotes: TableRowData[] = useMemo(() => {
    if (!site?.quotations) return [];
    return site.quotations.map((quote: CustomerQuotation) => ({
      id: quote.id,
      reference: quote.reference,
      customer: site.customer?.fullName || "-",
      site: site?.name || "-",
      amount: `TZS ${parseFloat(quote.totalAmount || quote.amount || "0").toLocaleString()}`,
      status: quote.status,
      raw_data: quote,
    }));
  }, [site]);

  const invoices: TableRowData[] = useMemo(() => {
    if (!site?.invoices) return [];
    return site.invoices.map((invoice: CustomerInvoice) => ({
      id: invoice.id,
      reference: invoice.reference,
      customer: site.customer?.fullName || "-",
      site: site?.name || "-",
      issueDate: format(new Date(invoice.issueDate), "MMM dd, yyyy"),
      dueDate: format(new Date(invoice.dueDate), "MMM dd, yyyy"),
      amount: `TZS ${parseFloat(invoice.totalAmount || invoice.amount || "0").toLocaleString()}`,
      status: invoice.status,
      raw_data: invoice,
    }));
  }, [site]);

  const movementsTotalItems = movementsQuery.data?.meta.total ?? 0;
  const movementsTotalPages = movementsQuery.data?.meta.totalPages ?? 1;

  const movements: TableRowData[] = useMemo(() => {
    const data = movementsQuery.data?.data ?? [];
    return data.map((movement: MovementActivity) => ({
      id: movement.id,
      reference: movement.reference,
      date: format(
        new Date(movement.plannedDate || movement.createdAt),
        "MMM dd, yyyy",
      ),
      initiator: movement.initiator?.fullName || "-",
      source: getMovementSourceDisplayName(movement) || "-",
      destination: getMovementDestinationDisplayName(movement) || "-",
      activity_type: movement.activityType,
      activity_sub_type: movement.activitySubType,
      project_type: movement.projectType ?? "—",
      status: movement.status,
      raw_data: movement,
    }));
  }, [movementsQuery.data]);

  const downloadEquipmentsExcel = () => {
    const siteName =
      (site?.name ?? selectedSite.replace(/^Select Site$/i, "").trim()) ||
      "Site";
    const customerName = site?.customer?.fullName?.trim() ?? "";
    const titleLine = customerName ? `${customerName} - ${siteName}` : siteName;
    const safeTitle = titleLine.replace(/[^a-zA-Z0-9-_]/g, "_");
    const headers = [
      "Item Code",
      "Description",
      "Unit Weight (KG)",
      "Total Quantity",
      "Good Quantity",
      "Damaged Quantity",
      "Total Weight",
    ];
    const dataRows = inventoryItems.map((item) => {
      const hasLength =
        item.inventory.length != null &&
        item.inventory.length !== "" &&
        String(item.inventory.length) !== "undefined";
      const description = hasLength
        ? `${item.inventory.description ?? ""} ${item.inventory.length}`.trim()
        : (item.inventory.description ?? "");
      const uw = item.inventory.unitWeight;
      const qty = item.totalQuantity ?? 0;
      const totalWeight =
        uw != null && uw !== "" && qty
          ? (() => {
              const n = Number(uw);
              return Number.isFinite(n) ? n * qty : null;
            })()
          : null;
      return [
        item.inventory.itemCode ?? "—",
        description || "—",
        uw != null && uw !== "" ? String(uw) : "—",
        item.totalQuantity ?? 0,
        item.goodQuantity ?? 0,
        item.damagedQuantity ?? 0,
        totalWeight != null
          ? totalWeight.toLocaleString(undefined, { maximumFractionDigits: 2 })
          : "—",
      ];
    });
    const grandTotal = inventoryItems.reduce((sum, item) => {
      const uw = item.inventory?.unitWeight;
      const qty = item.totalQuantity ?? 0;
      if (uw == null || uw === "" || !qty) return sum;
      const n = Number(uw);
      return sum + (Number.isFinite(n) ? n * qty : 0);
    }, 0);
    const grandTotalFormatted =
      grandTotal > 0
        ? grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : "—";
    const aoa = [
      [`Equipments - ${titleLine}`],
      [`Generated on ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}`],
      [""],
      headers,
      ...dataRows,
      [""],
      ["Grand Total Weight", "", "", "", "", "", grandTotalFormatted],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [
      { wch: 14 },
      { wch: 36 },
      { wch: 14 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Equipments");
    const fileName = `Equipments_${safeTitle}_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Equipments downloaded");
  };

  const downloadMovementsExcel = async () => {
    if (!siteId) return;
    const siteName =
      (site?.name ?? selectedSite.replace(/^Select Site$/i, "").trim()) ||
      "Site";
    const customerName = site?.customer?.fullName?.trim() ?? "";
    const titleLine = customerName ? `${customerName} - ${siteName}` : siteName;

    try {
      const limit = Math.max(movementsTotalItems || 0, 1);
      const movementsData = await getSiteMovements(
        siteId,
        1,
        limit,
        movementSubTypeParam,
        movementStatusParam,
        true,
      );
      const sortedMovements = sortMovementsForExcel(
        movementsData.data as MovementActivity[],
      );
      const M = sortedMovements.length;
      const W = movementExcelWidth(M);

      const prefix3: (string | number)[] = ["", "", ""];
      const tailForHeaderRow = (contentLen: number) =>
        Array(Math.max(0, W - contentLen)).fill("");

      const directionCells = sortedMovements.map((m) =>
        movementExcelFromLabel(m, siteId),
      );
      const vehicleCells = sortedMovements.map((m) =>
        String(m.vehicleNumber ?? m.vehicle_number ?? "").trim(),
      );
      const refCells = sortedMovements.map((m) =>
        String(m.reference ?? "").trim(),
      );
      const plannedDateCells = sortedMovements.map((m) => {
        const raw = m.plannedDate || m.createdAt;
        if (raw == null || raw === "") return "";
        const t = new Date(raw as string | Date).getTime();
        if (!Number.isFinite(t)) return "";
        return format(new Date(raw as string | Date), "dd/MM/yyyy");
      });

      const headerBlockRow1 = padMovementExcelRow(
        [
          ...prefix3,
          ...directionCells,
          ...tailForHeaderRow(prefix3.length + directionCells.length),
        ],
        W,
      );
      const headerBlockRow2 = padMovementExcelRow(
        [
          ...prefix3,
          ...vehicleCells,
          ...tailForHeaderRow(prefix3.length + vehicleCells.length),
        ],
        W,
      );
      const headerBlockRow3 = padMovementExcelRow(
        [
          ...prefix3,
          ...refCells,
          ...tailForHeaderRow(prefix3.length + refCells.length),
        ],
        W,
      );
      const headerBlockRow4 = padMovementExcelRow(
        [
          "PLANNED DATE",
          "",
          "",
          ...plannedDateCells,
          ...tailForHeaderRow(3 + plannedDateCells.length),
        ],
        W,
      );

      const aggregated = aggregateMovementItemsByMovements(
        sortedMovements,
        siteId,
      );

      let grandTotalWeight = 0;
      const dataRows: (string | number)[][] = [];
      let rowNo = 1;
      for (const row of aggregated) {
        const total = row.quantityByMovement.reduce((a, b) => a + b, 0);
        const uw = row.unitWeight;
        let lineTotalWeight: number | "" = "";
        if (uw != null && Number.isFinite(uw) && total !== 0) {
          const w = uw * total;
          grandTotalWeight += w;
          lineTotalWeight = Math.round(w * 1000) / 1000;
        }
        dataRows.push(
          padMovementExcelRow(
            [
              rowNo,
              row.description,
              row.size,
              ...row.quantityByMovement,
              total,
              uw != null && Number.isFinite(uw) ? uw : "",
              lineTotalWeight === "" ? "" : lineTotalWeight,
            ],
            W,
          ),
        );
        rowNo += 1;
      }

      const quantityHeaders = sortedMovements.map((m) =>
        getQuantityColumnName(m.activitySubType || "").toUpperCase(),
      );
      const headerRow = padMovementExcelRow(
        [
          "NO.",
          "DESCRIPTION OF MATERIALS",
          "SIZE",
          ...quantityHeaders,
          "TOTAL",
          "UNIT WEIGHT",
          "TOTAL WEIGHT",
        ],
        W,
      );

      const kgRowArr = Array(W).fill("");
      kgRowArr[W - 2] = "KG";
      kgRowArr[W - 1] =
        grandTotalWeight !== 0 ? Math.round(grandTotalWeight * 1e6) / 1e6 : "";
      const kgRow = padMovementExcelRow(kgRowArr, W);

      const tonsRowArr = Array(W).fill("");
      tonsRowArr[1] = "TOTAL WEIGHT SUPPLIED";
      tonsRowArr[W - 2] = "TONS";
      tonsRowArr[W - 1] =
        grandTotalWeight !== 0
          ? Math.round((grandTotalWeight / 1000) * 1e6) / 1e6
          : "";
      const tonsRow = padMovementExcelRow(tonsRowArr, W);

      const aoa = [
        padMovementExcelRow([titleLine], W),
        headerBlockRow1,
        headerBlockRow2,
        headerBlockRow3,
        headerBlockRow4,
        headerRow,
        ...dataRows,
        padMovementExcelRow([], W),
        kgRow,
        tonsRow,
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      /** Title row (customer + site): merge first 10 columns (A–J). */
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }];
      ws["!cols"] = [
        { wch: 6 },
        { wch: 40 },
        { wch: 8 },
        ...Array(M).fill({ wch: 12 }),
        { wch: 10 },
        { wch: 12 },
        { wch: 14 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const fileName = `Movement_Items_${siteName.replace(/[^a-zA-Z0-9-_]/g, "_")}_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Movement items downloaded");
    } catch (error) {
      toast.error(
        getBackendErrorMessage(error, "Failed to download movements"),
      );
    }
  };

  const resetBulkExtendForm = () => {
    setExtendableMovements([]);
    setSelectedExtendMovementIds([]);
    setBulkExtendDateTo("");
    setBulkExtendNotes("");
  };

  const loadExtendableMovements = async (): Promise<boolean> => {
    if (!siteId) return false;
    setLoadingExtendableMovements(true);
    resetBulkExtendForm();
    try {
      const limit = Math.max(movementsTotalItems || 0, 1);
      const movementsData = await getSiteMovements(siteId, 1, limit);
      const eligible = (movementsData.data as MovementActivity[]).filter(
        canExtendMovementDaysHired,
      );
      setExtendableMovements(eligible);
      if (eligible.length === 0) {
        toast.error(
          "No eligible movements to extend (DELIVER / TRANSFER outflow, not cancelled)",
        );
      }
      return true;
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to load movements"));
      return false;
    } finally {
      setLoadingExtendableMovements(false);
    }
  };

  const openBulkExtendDaysModal = async () => {
    if (!siteId) return;
    setShowExtendDaysModal(true);
    const loaded = await loadExtendableMovements();
    if (!loaded) setShowExtendDaysModal(false);
  };

  const toggleExtendMovement = (id: string, checked: boolean) => {
    setSelectedExtendMovementIds((prev) =>
      checked
        ? prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((item) => item !== id),
    );
  };

  const toggleSelectAllExtendMovements = (checked: boolean) => {
    setSelectedExtendMovementIds(
      checked ? extendableMovements.map((movement) => movement.id) : [],
    );
  };

  const bulkExtendMinDate = getExtensionMinDate(
    selectedExtendMovementIds,
    extendableMovements,
  );
  const isBulkExtendInvalid =
    selectedExtendMovementIds.length === 0 ||
    !bulkExtendDateTo ||
    selectedExtendMovementIds.some((movementId) =>
      isExtensionPeriodInvalid(
        extendableMovements.find((item) => item.id === movementId),
        bulkExtendDateTo,
      ),
    );

  /** Resolves true only when every selected movement was extended. */
  const handleBulkExtendDaysHired = async (): Promise<boolean> => {
    if (!selectedExtendMovementIds.length) {
      toast.error("Select at least one movement");
      return false;
    }
    if (!bulkExtendDateTo) {
      toast.error("Date To is required");
      return false;
    }

    const selectedMovements = extendableMovements.filter((movement) =>
      selectedExtendMovementIds.includes(movement.id),
    );

    for (const movement of selectedMovements) {
      const dateFrom = getDefaultExtensionDateFrom(movement);
      const calculatedDays = inclusiveDaysBetween(dateFrom, bulkExtendDateTo);
      if (!dateFrom || calculatedDays == null || calculatedDays < 1) {
        const ref = movement.reference?.trim() || movement.id.slice(0, 8);
        toast.error(`Invalid extension period for movement ${ref}`);
        return false;
      }
    }

    setIsExtendingDaysHired(true);
    try {
      const results = await Promise.allSettled(
        selectedMovements.map((movement) =>
          extendMovementDaysHired(movement.id, {
            dateFrom: getDefaultExtensionDateFrom(movement),
            dateTo: bulkExtendDateTo,
            notes: bulkExtendNotes.trim() || undefined,
          }),
        ),
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;

      await queryClient.invalidateQueries({
        queryKey: ["site", siteId, "movements"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["site", siteId, "statistics"],
      });

      if (failed === 0) {
        toast.success(
          `Extended days hired for ${succeeded} movement${succeeded === 1 ? "" : "s"}`,
        );
        setShowExtendDaysModal(false);
        resetBulkExtendForm();
        return true;
      } else if (succeeded === 0) {
        toast.error("Failed to extend days hired for the selected movements");
      } else {
        toast.error(
          `Extended ${succeeded} movement${succeeded === 1 ? "" : "s"}; ${failed} failed`,
        );
      }
      return false;
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to extend days hired"));
      return false;
    } finally {
      setIsExtendingDaysHired(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectingQuote || !siteId) return;
    setIsRejecting(true);
    try {
      await updateQuotationStatus(rejectingQuote.id as string, "REJECTED");
      setShowRejectModal(false);
      setRejectingQuote(null);
      await queryClient.invalidateQueries({ queryKey: ["site", siteId] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to reject quotation"));
    } finally {
      setIsRejecting(false);
    }
  };

  const handleConfirmApprove = async () => {
    if (!approvingQuote || !siteId) return;
    setIsApproving(true);
    try {
      await updateQuotationStatus(approvingQuote.id as string, "APPROVED");
      setShowApproveModal(false);
      const q = approvingQuote;
      setApprovingQuote(null);
      // navigate to create invoice with quotation prefilled
      navigate("/create-invoice", { state: { quotationId: q.id } });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to approve quotation"));
    } finally {
      setIsApproving(false);
    }
  };
  const fromSitesFilters = (
    location.state as { fromSitesFilters?: SitesFiltersState }
  )?.fromSitesFilters;
  const breadcrumbItems = [
    {
      label: "Sites",
      href: "/sites",
      state:
        fromSitesFilters != null
          ? { sitesFilters: fromSitesFilters }
          : undefined,
    },
    { label: "Site Details" },
  ];

  const goBackToSites = () => {
    navigate("/sites", {
      state:
        fromSitesFilters != null
          ? { sitesFilters: fromSitesFilters }
          : undefined,
    });
  };

  const refreshSite = () =>
    queryClient.invalidateQueries({ queryKey: ["site", siteId] });

  const statItems: StatItem[] = [
    {
      label: "Quotations",
      value: statistics?.quotations.count.toString() || "0",
      description: `TZS ${(statistics?.quotations.totalAmount || 0).toLocaleString()}`,
      icon: <Icons.Quotes className="w-5 h-5 text-primary-600" />,
    },
    {
      label: "Invoices",
      value: statistics?.invoices.count.toString() || "0",
      description: `Total: TZS ${(statistics?.invoices.totalAmount || 0).toLocaleString()}`,
      icon: <Wallet className="w-5 h-5 text-primary-600" />,
    },
    {
      label: "Unpaid Amount",
      value: `TZS ${(statistics?.invoices.unpaid || 0).toLocaleString()}`,
      description: `Paid: TZS ${(statistics?.invoices.paid || 0).toLocaleString()}`,
      icon: <Wallet className="w-5 h-5 text-red-600" />,
    },
    {
      label: "Total Movements",
      value: statistics?.movements.total.toString() || "0",
      description: `Quantity: ${statistics?.movements.totalQuantity || 0}`,
      icon: <Wrench className="w-5 h-5 text-blue-600" />,
    },
  ];

  const movementsGraph = (
    <HorizontalBarGraph
      graphTitle="Movements by Activity Sub Type"
      data={
        statistics?.movements.byActivitySubType.map((item) => ({
          category: item.activitySubType,
          gap: 0.2,
          values: [
            {
              label: "Count",
              value: item.count,
              fillColor: "#bce3ff",
            },
            {
              label: "Quantity",
              value: item.quantity,
              fillColor: "#e9d5ff",
            },
          ],
        })) || []
      }
    />
  );

  const billingGraph = (
    <BarGraph
      graphTitle="Quotes, Invoices, and Payments"
      metrics={[
        {
          label: "Quotes",
          count: statistics?.quotations.count || 0,
          percentage:
            statistics &&
            statistics.quotations.count + statistics.invoices.count > 0
              ? Math.round(
                  (statistics.quotations.count /
                    (statistics.quotations.count + statistics.invoices.count)) *
                    100,
                )
              : 0,
        },
        {
          label: "Invoices",
          count: statistics?.invoices.count || 0,
          percentage:
            statistics &&
            statistics.quotations.count + statistics.invoices.count > 0
              ? Math.round(
                  (statistics.invoices.count /
                    (statistics.quotations.count + statistics.invoices.count)) *
                    100,
                )
              : 0,
        },
        {
          label: "Paid",
          count: statistics?.invoices.paid || 0,
          percentage:
            statistics?.invoices.totalAmount &&
            statistics.invoices.totalAmount > 0
              ? Math.round(
                  (statistics.invoices.paid / statistics.invoices.totalAmount) *
                    100,
                )
              : 0,
        },
      ].sort((a, b) => b.count - a.count)}
    />
  );

  const equipmentRows: TableRowData[] = inventoryItems.map((item) => {
    const hasLength =
      item.inventory.length != null &&
      item.inventory.length !== "" &&
      String(item.inventory.length) !== "undefined";
    const description = hasLength
      ? `${item.inventory.description ?? ""} ${item.inventory.length}`.trim()
      : (item.inventory.description ?? "");
    const uw = item.inventory.unitWeight;
    const qty = item.totalQuantity ?? 0;
    const totalWeight =
      uw != null && uw !== "" && qty
        ? (() => {
            const n = Number(uw);
            return Number.isFinite(n) ? n * qty : null;
          })()
        : null;
    return {
      id: item.inventoryId,
      item_code: item.inventory.itemCode,
      description,
      quantity: item.totalQuantity,
      unit_weight: item.inventory.unitWeight,
      available: item.goodQuantity,
      damaged: item.damagedQuantity,
      total_weight:
        totalWeight != null
          ? totalWeight.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })
          : "—",
      raw_data: item.inventory,
    };
  });

  const equipmentGrandTotalWeight = (() => {
    const total = inventoryItems.reduce((sum, item) => {
      const uw = item.inventory?.unitWeight;
      const qty = item.totalQuantity ?? 0;
      if (uw == null || uw === "" || !qty) return sum;
      const n = Number(uw);
      return sum + (Number.isFinite(n) ? n * qty : 0);
    }, 0);
    return total > 0
      ? total.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : "—";
  })();

  const isPageLoading =
    siteQuery.isLoading ||
    inventoryItemsQuery.isLoading ||
    statisticsQuery.isLoading ||
    movementsQuery.isLoading;

  const equipmentActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedItemDetails(row as Inventory);
      },
    },
  ];
  const movementActivitiesActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedActivityDetails(row);
      },
    },
  ];
  const quotesActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedQuoteDetails(row);
      },
    },
    {
      label: "Duplicate",
      icon: Icons.Copy,
      onClick: (row: TableRowData) => {
        navigate("/create-quotation", {
          state: { duplicateQuotationId: row.id },
        });
      },
    },
    {
      label: "Edit",
      icon: Icons.Preview,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() === "PENDING",
      onClick: (row: TableRowData) => {
        navigate("/create-quotation", { state: { quotationId: row.id } });
      },
    },
    {
      label: "Approve",
      icon: Icons.Check,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() !== "APPROVED" &&
        String(row.status).toUpperCase() !== "REJECTED",
      onClick: (row: TableRowData) => {
        setApprovingQuote(row);
        setShowApproveModal(true);
      },
    },

    {
      label: "Reject",
      icon: Icons.Close,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() === "PENDING",
      onClick: (row: TableRowData) => {
        setRejectingQuote(row);
        setShowRejectModal(true);
      },
    },
  ];
  const billingActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedInvoiceDetails(row);
      },
    },
    {
      label: "Duplicate",
      icon: Icons.Copy,
      onClick: (row: TableRowData) => {
        navigate("/create-invoice", { state: { duplicateInvoiceId: row.id } });
      },
    },
    {
      label: "Edit",
      icon: Edit2,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() !== "PAID",
      onClick: (row: TableRowData) => {
        navigate("/create-invoice", { state: { invoiceId: row.id } });
      },
    },
    {
      label: "Mark as Paid",
      icon: Icons.Check,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() !== "PAID",
      onClick: (row: TableRowData) => {
        setMarkingInvoice(row);
        setShowMarkPaidModal(true);
      },
    },
  ];

  return (
    <>
      <ItemDetailsDrawer
        selectedItemDetails={selectedItemDetails}
        onClose={() => {
          setSelectedItemDetails(null);
        }}
      />
      <ActivityDetailsDrawer
        selectedActivityDetails={selectedActivityDetails}
        onClose={() => {
          setSelectedActivityDetails(null);
        }}
        onRefresh={async () => {
          if (!siteId) return;
          try {
            await queryClient.invalidateQueries({
              queryKey: ["site", siteId, "movements"],
            });
            if (selectedActivityDetails?.id) {
              const updated = await getMovementActivity(
                String(selectedActivityDetails.id),
              );
              setSelectedActivityDetails((prev) =>
                prev
                  ? {
                      ...prev,
                      reference: updated.reference,
                      date: format(
                        new Date(updated.plannedDate || updated.createdAt),
                        "MMM dd, yyyy",
                      ),
                      initiator: updated.initiator?.fullName || "-",
                      source: getMovementSourceDisplayName(updated) || "-",
                      destination:
                        getMovementDestinationDisplayName(updated) || "-",
                      activity_type: updated.activityType,
                      activity_sub_type: updated.activitySubType,
                      project_type: updated.projectType ?? "—",
                      status: updated.status,
                      raw_data: updated,
                    }
                  : prev,
              );
            }
          } catch (error) {
            toast.error(
              getBackendErrorMessage(error, "Failed to refresh movements"),
            );
          }
        }}
      />
      <InvoiceDrawer
        selectedInvoiceDetails={selectedInvoiceDetails}
        invoiceDetails={selectedInvoiceDetails}
        onClose={() => {
          setSelectedInvoiceDetails(null);
        }}
      />
      <QuoteDrawer
        selectedQuoteDetails={selectedQuoteDetails}
        quoteDetails={selectedQuoteDetails}
        onClose={() => {
          setSelectedQuoteDetails(null);
        }}
      />
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Confirm Reject"
        description={
          rejectingQuote
            ? `Are you sure you want to reject quotation ${rejectingQuote.reference || rejectingQuote.id}? This action will mark the quotation as REJECTED.`
            : "Are you sure you want to reject this quotation?"
        }
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white"
              onClick={handleConfirmReject}
              disabled={isRejecting}
            >
              {isRejecting ? "Rejecting..." : "Confirm Reject"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Confirm Approve"
        description={
          approvingQuote
            ? `Approve quotation ${approvingQuote.reference || approvingQuote.id}? This will mark it as APPROVED and open the Create Invoice page prefilled with the quotation.`
            : "Are you sure you want to approve this quotation?"
        }
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowApproveModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary-600 text-white"
              onClick={handleConfirmApprove}
              disabled={isApproving}
            >
              {isApproving ? "Approving..." : "Confirm Approve"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showMarkPaidModal}
        onClose={() => setShowMarkPaidModal(false)}
        title="Confirm Mark as Paid"
        description={
          markingInvoice
            ? `Are you sure you want to mark invoice ${markingInvoice.reference || markingInvoice.id} as PAID?`
            : "Are you sure?"
        }
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowMarkPaidModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary-600 text-white"
              onClick={async () => {
                if (!markingInvoice || !siteId) return;
                setIsMarkingPaid(true);
                try {
                  await updateInvoice(markingInvoice.id as string, {
                    status: "PAID",
                  });
                  setShowMarkPaidModal(false);
                  setMarkingInvoice(null);
                  await queryClient.invalidateQueries({
                    queryKey: ["site", siteId],
                  });
                } catch (error) {
                  toast.error(
                    getBackendErrorMessage(
                      error,
                      "Failed to mark invoice as paid",
                    ),
                  );
                } finally {
                  setIsMarkingPaid(false);
                }
              }}
              disabled={isMarkingPaid}
            >
              {isMarkingPaid ? "Processing..." : "Confirm Mark as Paid"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showExtendDaysModal}
        onClose={() => {
          if (!isExtendingDaysHired) {
            setShowExtendDaysModal(false);
            resetBulkExtendForm();
          }
        }}
        title="Extend Days Hired"
        description="Select eligible movements and an extension end date. Date from is calculated automatically for each movement from its hire period."
        className="max-w-2xl"
      >
        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm text-gray-600">Movements</Label>
              {extendableMovements.length > 0 && (
                <button
                  type="button"
                  className="text-sm text-primary-600 hover:underline disabled:opacity-50"
                  disabled={loadingExtendableMovements || isExtendingDaysHired}
                  onClick={() =>
                    toggleSelectAllExtendMovements(
                      selectedExtendMovementIds.length !==
                        extendableMovements.length,
                    )
                  }
                >
                  {selectedExtendMovementIds.length ===
                  extendableMovements.length
                    ? "Clear all"
                    : "Select all"}
                </button>
              )}
            </div>
            <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200 p-3 space-y-2">
              {loadingExtendableMovements ? (
                <p className="text-sm text-gray-500">Loading movements...</p>
              ) : extendableMovements.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No eligible movements found for this site.
                </p>
              ) : (
                extendableMovements.map((movement) => {
                  const checked = selectedExtendMovementIds.includes(
                    movement.id,
                  );
                  return (
                    <div key={movement.id} className="flex items-start gap-2">
                      <Checkbox
                        id={`extend-movement-${movement.id}`}
                        checked={checked}
                        disabled={isExtendingDaysHired}
                        onCheckedChange={(value) =>
                          toggleExtendMovement(movement.id, value === true)
                        }
                      />
                      <Label
                        htmlFor={`extend-movement-${movement.id}`}
                        className="cursor-pointer text-sm font-normal leading-5"
                      >
                        {formatExtendableMovementLabel(movement)}
                        {(() => {
                          const dateFrom =
                            getDefaultExtensionDateFrom(movement);
                          return dateFrom
                            ? ` · from ${format(new Date(`${dateFrom}T00:00:00`), "MMM dd, yyyy")}`
                            : "";
                        })()}
                      </Label>
                    </div>
                  );
                })
              )}
            </div>
            <p className="text-xs text-gray-500">
              {selectedExtendMovementIds.length} selected · DELIVER and TRANSFER
              outflow only
            </p>
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="bulk-extend-date-to"
              className="text-sm text-gray-600"
            >
              Date To
            </Label>
            <Input
              id="bulk-extend-date-to"
              type="date"
              className="h-9 max-w-[200px]"
              value={bulkExtendDateTo}
              min={bulkExtendMinDate}
              disabled={isExtendingDaysHired}
              onChange={(e) => setBulkExtendDateTo(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              Date from is set per movement (hire start, or the day after its
              current hire end).
            </p>
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="bulk-extend-notes"
              className="text-sm text-gray-600"
            >
              Notes (Optional)
            </Label>
            <Input
              id="bulk-extend-notes"
              className="h-9"
              value={bulkExtendNotes}
              disabled={isExtendingDaysHired}
              onChange={(e) => setBulkExtendNotes(e.target.value)}
              placeholder="Reason for extension"
            />
          </div>

          {bulkExtendDateTo && selectedExtendMovementIds.length > 0 && (
            <div className="rounded-md border border-gray-100 bg-gray-50 p-3 space-y-1.5">
              <p className="text-sm font-medium text-gray-800">
                Extension preview
              </p>
              {selectedExtendMovementIds.map((movementId) => {
                const movement = extendableMovements.find(
                  (item) => item.id === movementId,
                );
                if (!movement) return null;
                const dateFrom = getDefaultExtensionDateFrom(movement);
                const calculatedDays = inclusiveDaysBetween(
                  dateFrom,
                  bulkExtendDateTo,
                );
                const currentDays = Number(
                  movement.daysHired ?? (movement as any).days_hired ?? 0,
                );
                return (
                  <p key={movementId} className="text-sm text-gray-600">
                    {formatExtendableMovementLabel(movement)}:{" "}
                    <span className="font-medium text-gray-900">
                      {calculatedDays ?? "—"} day
                      {calculatedDays === 1 ? "" : "s"}
                    </span>
                    {calculatedDays != null && Number.isFinite(currentDays) && (
                      <>
                        {" "}
                        · new total{" "}
                        <span className="font-medium text-gray-900">
                          {currentDays + calculatedDays}
                        </span>
                      </>
                    )}
                  </p>
                );
              })}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              disabled={isExtendingDaysHired}
              onClick={() => {
                setShowExtendDaysModal(false);
                resetBulkExtendForm();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                isExtendingDaysHired ||
                loadingExtendableMovements ||
                isBulkExtendInvalid
              }
              onClick={() => void handleBulkExtendDaysHired()}
            >
              {isExtendingDaysHired ? "Extending..." : "Extend selected"}
            </Button>
          </div>
        </div>
      </Modal>

      {isMobile ? (
        <SiteDetailsMobile
          loading={isPageLoading}
          title={selectedSite || "Site Details"}
          site={site}
          onBack={goBackToSites}
          onRefresh={refreshSite}
          onNewInvoice={() => navigate("/create-invoice")}
          stats={statItems}
          insights={
            <>
              {movementsGraph}
              {billingGraph}
            </>
          }
          equipment={{
            rows: equipmentRows,
            actions: equipmentActions,
            grandTotalWeight: equipmentGrandTotalWeight,
            onDownload: downloadEquipmentsExcel,
          }}
          movements={{
            rows: movements,
            actions: movementActivitiesActions,
            totalItems: movementsTotalItems,
            isFetching: movementsQuery.isFetching && !movementsQuery.isLoading,
            pagination: {
              page: movementsPage,
              pageSize: movementsPageSize,
              totalItems: movementsTotalItems,
              totalPages: movementsTotalPages,
              onPageChange: setMovementsPage,
              onPageSizeChange: setMovementsPageSize,
            },
            subTypeFilter: movementSubTypeFilter,
            onSubTypeFilterChange: setMovementSubTypeFilter,
            statusFilter: movementStatusFilter,
            onStatusFilterChange: setMovementStatusFilter,
            onDownload: downloadMovementsExcel,
          }}
          quotes={{ rows: quotes, actions: quotesActions }}
          invoices={{ rows: invoices, actions: billingActions }}
          extend={{
            movements: extendableMovements,
            loading: loadingExtendableMovements,
            selectedIds: selectedExtendMovementIds,
            onToggle: toggleExtendMovement,
            onToggleAll: toggleSelectAllExtendMovements,
            load: loadExtendableMovements,
            reset: resetBulkExtendForm,
            dateTo: bulkExtendDateTo,
            onDateToChange: setBulkExtendDateTo,
            notes: bulkExtendNotes,
            onNotesChange: setBulkExtendNotes,
            minDate: bulkExtendMinDate,
            isInvalid: isBulkExtendInvalid,
            isSubmitting: isExtendingDaysHired,
            submit: handleBulkExtendDaysHired,
          }}
        />
      ) : isPageLoading ? (
        <PageLoadingState showAnalytics={true} />
      ) : (
        <div className="h-full">
          <div className="relative flex flex-1 flex-col bg-white rounded-l-[12px] overflow-hidden border-l border-gray-100">
            <div className="border-b px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="space-y-4">
                  <Breadcrumb items={breadcrumbItems} />
                  <h1 className="text-2xl font-bold font-jakarta">
                    {selectedSite || "Site Details"}
                  </h1>
                </div>
                <div className="flex items-center gap-2">
                  <RefreshToolbarButton
                    isRefreshing={
                      siteQuery.isFetching ||
                      inventoryItemsQuery.isFetching ||
                      statisticsQuery.isFetching ||
                      movementsQuery.isFetching
                    }
                    onRefresh={refreshSite}
                  />
                  <Button
                    type="button"
                    className="h-9 text-sm bg-primary-600 hover:bg-primary-700 text-white"
                    onClick={() => {
                      navigate("/create-invoice");
                    }}
                  >
                    New Invoice
                  </Button>
                  <div className="h-4 w-px bg-gray-200 mx-2" />
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-9 w-9 p-0 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    onClick={goBackToSites}
                  >
                    <Icons.X className="h-4 w-4 cursor-pointer" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {statItems.map((item, index) => {
                  return (
                    <StatisticsCard
                      key={index}
                      label={item.label}
                      value={item.value}
                      description={item.description}
                      icon={item.icon}
                    />
                  );
                })}
              </div>

              <div className="grid grid-cols-[1fr,400px] gap-4">
                {movementsGraph}
                {billingGraph}
              </div>

              <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                <div
                  className={cn(
                    "bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer",
                    { "rounded-2xl": !showEquipments },
                  )}
                  onClick={() => setShowEquipments(!showEquipments)}
                >
                  <div className="flex items-center justify-between gap-20">
                    <h6 className="font-bold text-lg">
                      Equipments
                      {inventoryItems.length > 0
                        ? ` (${inventoryItems.length})`
                        : ""}
                    </h6>
                    {showEquipments ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
                {showEquipments && (
                  <div className="mb-4 bg-white pt-4 px-4 space-y-3">
                    <div className="flex justify-end mb-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={downloadEquipmentsExcel}
                        disabled={inventoryItems.length === 0}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Excel
                      </Button>
                    </div>
                    <DataTable
                      columns={siteInventoryTableColumns}
                      data={equipmentRows}
                      actions={equipmentActions}
                    />
                    {inventoryItems.length > 0 && (
                      <div className="flex justify-end border-t border-gray-200 pt-3 mt-1">
                        <span className="font-semibold text-gray-700">
                          Grand Total Weight: {equipmentGrandTotalWeight}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                <div
                  className={cn(
                    "bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer",
                    { "rounded-2xl": !showMovements },
                  )}
                  onClick={() => setShowMovements(!showMovements)}
                >
                  <div className="flex items-center justify-between gap-20">
                    <h6 className="font-bold text-lg">
                      Movements
                      {movementsTotalItems > 0
                        ? ` (${movementsTotalItems})`
                        : ""}
                    </h6>
                    {showMovements ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
                {showMovements && (
                  <div className="mb-4 bg-white pt-4 px-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <SelectInput
                          value={movementSubTypeFilter}
                          options={[
                            { label: "All", value: "all" },
                            { label: "Deliver", value: "DELIVER" },
                            { label: "Return", value: "RETURN" },
                            { label: "Transfer", value: "TRANSFER" },
                            { label: "Purchase", value: "PURCHASE" },
                            { label: "Sell", value: "SELL" },
                            { label: "Dispose", value: "DISPOSE" },
                            { label: "Lost", value: "LOST" },
                          ]}
                          placeholder="Subtype"
                          onChange={setMovementSubTypeFilter}
                        />
                        <SelectInput
                          value={movementStatusFilter}
                          options={[
                            { label: "All", value: "all" },
                            { label: "Draft", value: "DRAFT" },
                            { label: "Initiated", value: "INITIATED" },
                            { label: "Approved", value: "APPROVED" },
                            { label: "Closed", value: "CLOSED" },
                            { label: "Cancelled", value: "CANCELLED" },
                          ]}
                          placeholder="Status"
                          onChange={setMovementStatusFilter}
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void openBulkExtendDaysModal()}
                          disabled={movementsTotalItems === 0}
                        >
                          Extend Days Hired
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={downloadMovementsExcel}
                          disabled={movementsTotalItems === 0}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Excel
                        </Button>
                      </div>
                    </div>
                    <DataTable
                      columns={activitiesTableColumns}
                      data={movements}
                      actions={movementActivitiesActions}
                      pagination={{
                        page: movementsPage,
                        pageSize: movementsPageSize,
                        totalItems: movementsTotalItems,
                        totalPages: movementsTotalPages,
                        onPageChange: setMovementsPage,
                        onPageSizeChange: setMovementsPageSize,
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                <div
                  className={cn(
                    "bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer",
                    { "rounded-2xl": !showQuotes },
                  )}
                  onClick={() => setShowQuotes(!showQuotes)}
                >
                  <div className="flex items-center justify-between gap-20">
                    <h6 className="font-bold text-lg">
                      Quotes
                      {quotes.length > 0 ? ` (${quotes.length})` : ""}
                    </h6>
                    {showQuotes ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
                {showQuotes && (
                  <div className="mb-4 bg-white pt-4 px-4 space-y-3">
                    <DataTable
                      columns={quotesTableColumns}
                      data={quotes}
                      actions={quotesActions}
                    />
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                <div
                  className={cn(
                    "bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer",
                    { "rounded-2xl": !showInvoices },
                  )}
                  onClick={() => setShowInvoices(!showInvoices)}
                >
                  <div className="flex items-center justify-between gap-20">
                    <h6 className="font-bold text-lg">
                      Invoices
                      {invoices.length > 0 ? ` (${invoices.length})` : ""}
                    </h6>
                    {showInvoices ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
                {showInvoices && (
                  <div className="mb-4 bg-white pt-4 px-4 space-y-3">
                    <DataTable
                      columns={invoicesTableColumns}
                      data={invoices}
                      actions={billingActions}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteDetails;
